import { Box, Button, Input } from "@chakra-ui/react";
import { ColorIcon, colors, Field } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { useCallback, useState } from "react";
import { formatPhoneNumber } from "@/utils/formatIdentifyNumber";
import { useSubmitLead } from "@/hooks/useSubmitLead";
import { postLeadConsentRecord } from "@/apis/leadConsentRecord";
import { useGetServerTime } from "@/hooks/useGetServerTime";

const PRIVACY_CONSENT_URL = "https://goifuneral.notion.site/146a79013e6c40a9b4731dd07a8ad99d";
const MARKETING_CONSENT_URL = "https://goifuneral.notion.site/3d74caff865d48ccbf32d9a0ad3df80b";
const NIGHT_CONSENT_URL = "https://goifuneral.notion.site/3e893cfdb8584fb28a4086a60aef5ad4";

const MOURNER_COUNT_OPTIONS = ["0명(무빈소)", "50명 ~100명 내외", "100명 이상"];

/**
 * 예상 조문객수를 고르고 전화번호를 남기는 2단계 리드 모달.
 *
 * 장례식장 재방문(#318)과 요양병원→시설 장례 준비 모달이 폼·동의·완료화면까지
 * 같아서 카피와 슬랙 본문만 주입받는다. 원래 경로
 * (containers/Parlor/ParlorDetailContainer/components/ParlorRevisitLeadModal)는
 * re-export 로 남겨 뒀다.
 */
export interface MournerCountLeadModalCopy {
  /** 제목 위 작은 글씨. 없으면 미표시 */
  caption?: string;
  title: string;
  /** 1단계(조문객수 선택) 안내 문구 */
  step1Description: string;
  /** 2단계 제목. 없으면 1단계 헤더를 그대로 유지한다 */
  step2Title?: string;
  /** 2단계(번호 입력) 안내 문구. \n 은 줄바꿈으로 렌더된다 */
  step2Description: string;
  submitLabel: string;
}

export default function MournerCountLeadModal({
  copy,
  buildSlackMessage,
  forceOutsourcingNightCall,
  onClose,
  onSubmitted,
}: {
  copy: MournerCountLeadModalCopy;
  /** 선택한 조문객수로 #홈 질문 슬랙 본문을 만든다. 전화번호·헤더는 BE 가 붙인다 */
  buildSlackMessage: (mournerCount: string) => string;
  /**
   * 야간 장컴 인계에서 임박도 게이트만 건너뛴다.
   * 레포 전체에서 켜는 곳은 식장 재방문 비용문의뿐이다 (wiki/features/home-lead-v2.md).
   * 끌 때는 false 를 보내지 않고 필드를 통째로 빼는 것이 나머지 홈질문 리드의 관례다.
   */
  forceOutsourcingNightCall?: boolean;
  onClose: () => void;
  /** 제출 성공 시. 재노출 차단 기록과 화면별 customEvent 를 호출부가 담당한다 */
  onSubmitted?: () => void;
}) {
  const [step, setStep] = useState<1 | 2>(1);
  const [mournerCount, setMournerCount] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const { data: serverTime } = useGetServerTime();
  const { submitLead } = useSubmitLead();
  const isNight = serverTime?.is_night ?? false;

  const handleNext = useCallback(() => {
    if (!mournerCount) {
      alert("예상 조문객수를 선택해주세요.");
      return;
    }
    setStep(2);
  }, [mournerCount]);

  const handleSubmit = useCallback(async () => {
    if (loading) return;
    if (!/^010-\d{4}-\d{4}$/.test(phone)) {
      alert("번호 형식을 확인해주세요. (010-****-****)");
      return;
    }

    setLoading(true);
    try {
      await submitLead({
        phone,
        lead_type: "home",
        slack_message: buildSlackMessage(mournerCount),
        accept_night_call: isNight ? true : undefined,
        is_force_outsourcing_night_call: forceOutsourcingNightCall || undefined,
      });
      onSubmitted?.();
      setIsCompleted(true);
    } catch (error) {
      console.error("전송 실패:", error);
      alert("잠시 오류가 발생했습니다. 문제가 지속될 시 고객센터(1666-9784)로 연락주세요.");
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    phone,
    mournerCount,
    buildSlackMessage,
    forceOutsourcingNightCall,
    onSubmitted,
    isNight,
    submitLead,
  ]);

  const handleMarketingAgree = useCallback(() => {
    postLeadConsentRecord({
      phone,
      agreement: { marketing_agreed: true },
    }).catch((e) => console.error("마케팅 수신동의 기록 실패:", e));
    onClose();
  }, [phone, onClose]);

  const handleMarketingDecline = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <BaseStack width="100%" spacing={16} padding="20px">
      {isCompleted ? (
        <BaseStack width="100%" spacing={24}>
          <BaseStack direction="horizontal" width="100%" justifyContent="flex-end" spacing={0}>
            <Box cursor="pointer" onClick={handleMarketingDecline}>
              <ColorIcon name="close" width={28} height={28} color={colors.black} />
            </Box>
          </BaseStack>
          <BaseStack width="100%" spacing={12} alignItems="center">
            <Box className="subtitle_21_b gray900" textAlign="center">
              신청이 완료되었어요
            </Box>
            <Box className="caption_12_b gray700" textAlign="center">
              마케팅 정보 수신에 동의하시면
              <br />
              장례 관련 혜택, 이벤트 소식을 보내드려요.
            </Box>
          </BaseStack>
          <BaseStack
            direction="horizontal"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            spacing={0}
          >
            <BaseStack direction="horizontal" alignItems="center" spacing={4}>
              <Box className="caption_11_b" color={colors.gray400}>
                선택
              </Box>
              <Box className="caption_11_b gray700">마케팅 정보 수신 동의</Box>
            </BaseStack>
            <a
              className="caption_11_b"
              href={MARKETING_CONSENT_URL}
              target="_blank"
              rel="noreferrer"
            >
              보기
            </a>
          </BaseStack>
          <BaseStack direction="horizontal" width="100%" spacing={8}>
            <Box flex={1}>
              <Button
                width="100%"
                backgroundColor={colors.gray100}
                color={colors.gray700}
                onClick={handleMarketingDecline}
              >
                닫기
              </Button>
            </Box>
            <Box flex={1}>
              <Button
                width="100%"
                backgroundColor={colors.orange650}
                color={colors.white}
                style={{ backgroundColor: colors.orange650, color: colors.white }}
                onClick={handleMarketingAgree}
              >
                동의하기
              </Button>
            </Box>
          </BaseStack>
        </BaseStack>
      ) : (
        <>
          <BaseStack
            direction="horizontal"
            width="100%"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={8}
          >
            <BaseStack spacing={4}>
              {copy.caption && <Box className="caption_12_b gray600">{copy.caption}</Box>}
              <Box className="subtitle_21_b gray900">
                {step === 2 && copy.step2Title ? copy.step2Title : copy.title}
              </Box>
            </BaseStack>
            <Box cursor="pointer" flex="none" onClick={onClose}>
              <ColorIcon name="close" width={28} height={28} color={colors.black} />
            </Box>
          </BaseStack>

          {step === 1 ? (
            <BaseStack width="100%" spacing={28}>
              <Box className="body_14_r gray600">{copy.step1Description}</Box>
              <Field.Radio
                title=""
                items={MOURNER_COUNT_OPTIONS}
                value={mournerCount}
                onChange={setMournerCount}
                radioWidth="100%"
                className="body_14_r"
                flexDirection="column"
                alignItem="center"
              />
              <Button
                width="100%"
                backgroundColor={colors.orange650}
                color={colors.white}
                style={{ backgroundColor: colors.orange650, color: colors.white }}
                onClick={handleNext}
              >
                확인
              </Button>
            </BaseStack>
          ) : (
            <BaseStack width="100%" spacing={28}>
              <BaseStack width="100%" spacing={16}>
                <Box className="body_14_r gray600" whiteSpace="pre-line">
                  {copy.step2Description}
                </Box>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                  maxLength={13}
                  type="tel"
                  border={`1px solid ${colors.gray400}`}
                  width="100%"
                  placeholder="010-****-****"
                />
                <BaseStack width="100%" spacing={14}>
                  <BaseStack
                    direction="horizontal"
                    width="100%"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={0}
                  >
                    <BaseStack direction="horizontal" alignItems="center" spacing={4}>
                      <Box className="caption_11_b" color={colors.gray400}>
                        필수
                      </Box>
                      <Box className="caption_11_b gray700">개인정보 수집·이용·제공 동의</Box>
                    </BaseStack>
                    <a
                      className="caption_11_b"
                      href={PRIVACY_CONSENT_URL}
                      target="_blank"
                      rel="noreferrer"
                    >
                      보기
                    </a>
                  </BaseStack>
                  {isNight && (
                    <BaseStack
                      direction="horizontal"
                      width="100%"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={0}
                    >
                      <BaseStack direction="horizontal" alignItems="center" spacing={4}>
                        <Box className="caption_11_b" color={colors.gray400}>
                          필수
                        </Box>
                        <Box className="caption_11_b gray700">긴급 상황 시 수신 동의(야간)</Box>
                      </BaseStack>
                      <a
                        className="caption_11_b"
                        href={NIGHT_CONSENT_URL}
                        target="_blank"
                        rel="noreferrer"
                      >
                        보기
                      </a>
                    </BaseStack>
                  )}
                </BaseStack>
              </BaseStack>
              <Button
                width="100%"
                backgroundColor={colors.orange650}
                color={colors.white}
                style={{ backgroundColor: colors.orange650, color: colors.white }}
                onClick={handleSubmit}
                isLoading={loading}
                disabled={loading}
              >
                {copy.submitLabel}
              </Button>
            </BaseStack>
          )}
        </>
      )}
    </BaseStack>
  );
}
